import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Rocket, Landmark, Palette, Sparkles } from "lucide-react";

const CATEGORIES = [
  {
    id: "entrepreneurs",
    icon: Rocket,
    title: "Visionary Entrepreneurs",
    tag: "Founders & Builders",
    desc: "Scale, burnouts, equity and exits — the honest arithmetic behind building companies.",
    question: "\"What did your first crore actually cost you?\"",
    img: "https://images.unsplash.com/photo-1739298061709-cfc57e6b620e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwyfHxkaXZlcnNlJTIwYnVzaW5lc3MlMjBsZWFkZXJzJTIwZW50cmVwcmVuZXVycyUyMGFydGlzdHMlMjBzcGVha2luZ3xlbnwwfHx8fDE3ODc5MTAzNTZ8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "politicians",
    icon: Landmark,
    title: "Leaders & Policy Minds",
    tag: "Politicians & Public Capital",
    desc: "Nation building, macro-economics and the money of public life — beyond the podium.",
    question: "\"How does power change a person's relationship with money?\"",
    img: "https://images.unsplash.com/photo-1581455473784-85b5cd7a0567?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHw0fHxkaXZlcnNlJTIwYnVzaW5lc3MlMjBsZWFkZXJzJTIwZW50cmVwcmVuZXVycyUyMGFydGlzdHMlMjBzcGVha2luZ3xlbnwwfHx8fDE3ODc5MTAzNTZ8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "artists",
    icon: Palette,
    title: "Artists & Creators",
    tag: "The Cultural Quotient",
    desc: "Monetizing craft, creative freedom and the business of being original.",
    question: "\"When did your art start paying your bills — and what did it take?\"",
    img: "https://images.unsplash.com/photo-1648493696785-c0b27b0d93a5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHw0fHxjaGFyaXNtYXRpYyUyMHBvZGNhc3QlMjBob3N0JTIwcG9ydHJhaXQlMjBzdHVkaW98ZW58MHx8fHwxNzg3OTEwMzU2fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "gurus",
    icon: Sparkles,
    title: "Modern Gurus",
    tag: "Mindset & Inner Growth",
    desc: "Spiritual wealth, mind mastery and the quotient money can't measure.",
    question: "\"What is wealth, once the numbers stop mattering?\"",
    img: "https://images.unsplash.com/photo-1761638196352-67aff5c7c3d3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwzfHxkaXZlcnNlJTIwYnVzaW5lc3MlMjBsZWFkZXJzJTIwZW50cmVwcmVuZXVycyUyMGFydGlzdHMlMjBzcGVha2luZ3xlbnwwfHx8fDE3ODc5MTAzNTZ8MA&ixlib=rb-4.1.0&q=85",
  },
];

const FILTERS = [
  { id: "all", label: "All Walks of Life" },
  { id: "entrepreneurs", label: "Entrepreneurs" },
  { id: "politicians", label: "Politicians" },
  { id: "artists", label: "Artists" },
  { id: "gurus", label: "Gurus" },
];

const GuestDimensions = () => {
  const [active, setActive] = useState("all");
  const visible = active === "all" ? CATEGORIES : CATEGORIES.filter((c) => c.id === active);

  return (
    <section id="guest-dimensions" className="relative py-16 sm:py-24 px-4">
      <div className="ambient-glow w-[300px] h-[300px] sm:w-[480px] sm:h-[480px] bg-amber-500/10 top-40 left-1/2 -translate-x-1/2" />
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="text-center max-w-2xl mx-auto"
        >
          <p className="text-xs font-semibold tracking-[0.22em] uppercase text-amber-400">Guest Dimensions</p>
          <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
            One table. Every walk of life.
          </h2>
          <p className="mt-4 text-sm sm:text-base text-slate-400 leading-relaxed">
            From boardrooms to ashrams, campaign trails to canvas studios — if you have a
            money and growth story worth hearing, there is a chair here with your name on it.
          </p>
        </motion.div>

        <div className="mt-8 sm:mt-10 flex flex-wrap justify-center gap-2 sm:gap-3">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              data-testid={`guest-filter-${f.id}`}
              onClick={() => setActive(f.id)}
              className={`rounded-full px-4 sm:px-6 py-2.5 text-[10px] sm:text-xs font-semibold tracking-[0.08em] sm:tracking-[0.12em] uppercase transition-all duration-300 ${
                active === f.id
                  ? "tactile-btn"
                  : "glass-panel text-slate-400 hover:text-amber-300 hover:-translate-y-0.5"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <motion.div layout className="mt-10 sm:mt-14 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6">
          <AnimatePresence mode="popLayout">
            {visible.map((c) => (
              <motion.article
                key={c.id}
                layout
                initial={{ opacity: 0, scale: 0.92, y: 24 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.92, y: 24 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`guest-card-${c.id}`}
                className="clay-card overflow-hidden neu-lift group"
              >
                <div className="relative h-44 sm:h-48 overflow-hidden">
                  <img
                    src={c.img}
                    alt={c.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0e1420] via-transparent to-transparent" />
                  <span className="absolute top-4 left-4 clay-badge w-11 h-11 rounded-2xl flex items-center justify-center">
                    <c.icon className="w-5 h-5 text-amber-400" />
                  </span>
                </div>
                <div className="p-5 sm:p-6">
                  <p className="text-[10px] font-semibold tracking-[0.2em] uppercase text-amber-400/90">{c.tag}</p>
                  <h3 className="mt-2 font-display text-lg font-semibold">{c.title}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{c.desc}</p>
                  <div className="mt-5 glass-panel rounded-2xl px-4 py-3">
                    <p className="font-serif-quote text-xs text-slate-300 italic leading-relaxed">{c.question}</p>
                  </div>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

export default GuestDimensions;
